import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import DeleteButton from "../common/DeleteButton";
import ThreeDot from "../common/ThreeDot";
import EditButton from "../common/EditButton";
import {
  deleteBrand,
  getAllBrands,
  updateBrand,
} from "../../services/brand.service";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import ViewDetailsButton from "../common/ViewDetailsButton";
import { getAllBrandsGraphQL } from "../../services/graphql.service";
import { debounce } from "lodash";

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const { token } = useSelector((state) => state?.auth);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [totalEntries, setTotalEntries] = useState(null);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const backPage = parseInt(queryParams.get("page"));
  const backSearch = queryParams.get("search");
  const [currentPage, setCurrentPage] = useState(backPage || 1);
  const [search, setSearch] = useState(backSearch || "");
  const [debouncedSearch, setDebouncedSearch] = useState(backSearch || "");

  const fetchBrands = async (page = currentPage) => {
    try {
      const params = { page };
      const res = await getAllBrands(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        setBrands(res?.data?.results);
        setNext(res?.data?.next);
        setPrevious(res?.data?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.count || null);
        setTotalPages(Math.ceil((res?.data?.count || 0) / itemsPerPage));
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch brands. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchBrandsGraphQL = async (page = 1, search) => {
    console.log("Fetching brands:", { search, page });

    try {
      const res = await getAllBrandsGraphQL(token, page, search);

      if (res && res.status >= 200 && res.status < 300) {
        console.log("Brands fetched successfully:", res.data);

        setBrands(res?.data?.data?.search_brands?.results);
        setNext(res?.data?.data?.search_brands?.next);
        setPrevious(res?.data?.data?.search_brands?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.data?.search_brands?.count || null);
        setTotalPages(
          Math.ceil((res?.data?.data?.search_brands?.count || 0) / itemsPerPage)
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch brands. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearchHandler(value);
  };

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    if (debouncedSearch) {
      fetchBrandsGraphQL(currentPage, debouncedSearch);
    } else {
      fetchBrands(currentPage);
    }
  }, [debouncedSearch, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  const handleDelete = async (brandName, brandId) => {
    try {
      const res = await deleteBrand(token, brandId);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Deleted!",
          text: `${brandName} Brand has been deleted successfully.`,
          icon: "success",
        }).then(() => {
          const remainingItems = brands.length - 1;

          if (remainingItems === 0 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
            if (search) {
              fetchBrandsGraphQL(currentPage - 1, search);
            } else {
              fetchBrands(currentPage - 1);
            }
          } else {
            if (search) {
              fetchBrandsGraphQL(currentPage, search);
            } else {
              fetchBrands(currentPage);
            }
          }
        });
      } else {
        Swal.fire(
          "Error",
          res?.data?.detail || "Failed to delete the brand. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during brand deletion:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const handleStockChange = async (brandId, inStock) => {
    const data = {
      in_stock: inStock,
    };

    console.log("Updating stock status:", data);

    try {
      const res = await updateBrand(token, data, brandId);

      if (res && res.status >= 200 && res.status < 300) {
        if (search) {
          fetchBrandsGraphQL(currentPage, search);
        } else {
          fetchBrands(currentPage);
        }
      } else if (res && res.status >= 400 && res.status < 500) {
        console.error(
          "Validation error while updating stock status:",
          res?.data?.message || "Invalid input."
        );
      } else {
        console.error("Unexpected error during stock status update.");
      }
    } catch (error) {
      console.error("Error during stock status update:", error);
    }
  };

  return (
    <div className="content-body">
      {/* <!-- row --> */}
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder="Search here"
              value={search}
              onChange={(e) => handleSearchChange(e)}
            />
            <div className="input-group-append">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div>
          <div style={{ zIndex: 2 }}>
            <Link to="/brands/add">
              <button className="btn btn-primary px-3 py-2 add-btn sticky-btn">
                Add
                <img
                  className="plus-sign ms-2"
                  src="/assets/images/kidskart/icon/add.png"
                />
              </button>
            </Link>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-12 col-xxl-12 col-lg-12 col-sm-12">
            <div className="card">
              <div className="card-body p-0 ">
                {brands && brands.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table order-request2">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Brand Logo</th>
                          <th>Brand</th>
                          <th>Acronym</th>
                          <th>In Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brands?.map((item, i) => (
                          <tr key={item.brand_id}>
                            <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                            <td>
                              <div className="media media2 align-items-center">
                                <img
                                  className="img-fluid"
                                  width="75"
                                  src={item.brand_image_url || item.image}
                                  alt={item.brand_name}
                                />
                              </div>
                            </td>
                            <td>{item.brand_name}</td>
                            <td>{item.acronym || "N/A"}</td>
                            <td>
                              {/* {} */}

                              <div className="d-flex align-items-center">
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={item.in_stock}
                                    id="flexSwitchCheckDefault"
                                    onChange={(e) =>
                                      handleStockChange(
                                        item.brand_id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </div>
                                <div className="dropdown ms-auto c-pointer">
                                  <ThreeDot />
                                  <div className="dropdown-menu dropdown-menu-end">
                                    <EditButton
                                      to={`/brands/update/`}
                                      id={item.brand_id}
                                      page={currentPage}
                                      search={search}
                                    />
                                    <DeleteButton
                                      name={`${item.brand_name} Brand`}
                                      onDelete={() =>
                                        handleDelete(
                                          item.brand_name,
                                          item.brand_id
                                        )
                                      }
                                    />
                                    <ViewDetailsButton
                                      to={`/brands/details/`}
                                      id={item.brand_id}
                                      page={currentPage}
                                      search={search}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {brands && brands.length < 3 ? (
                          <tr style={{ height: "205px" }}></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="my-5 py-5 text-center fs-4 fw-bold">
                    Please Add Brand
                  </div>
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {totalEntries && totalEntries > 10 && (
              <div className="dataTables_wrapper no-footer">
                <div className="dataTables_info">
                  Showing {startEntry} to {endEntry} of {totalEntries} entries
                </div>
                <div className="dataTables_paginate paging_simple_numbers">
                  <button
                    className={`btn btn-primary me-2 ${
                      !previous && "disabled"
                    }`}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    {(() => {
                      const maxVisible = 7;
                      const pages = Array.from(
                        { length: totalPages },
                        (_, i) => i + 1
                      );
                      let start = Math.max(
                        currentPage - Math.floor(maxVisible / 2),
                        1
                      );
                      let end = start + maxVisible - 1;

                      if (end > totalPages) {
                        end = totalPages;
                        start = Math.max(end - maxVisible + 1, 1);
                      }

                      return pages.slice(start - 1, end).map((page) => (
                        <button
                          key={page}
                          className={`btn btn-sm mx-1 ${
                            currentPage === page
                              ? "btn-dark"
                              : "btn-outline-dark"
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                  </span>
                  <button
                    className={`btn btn-primary ms-2 ${!next && "disabled"}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
